import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight, Upload5, Trash2, ArrowRefresh2, File } from 'blaze-ui/icons'
import { Pdf, Markdown, TextFile } from 'blaze-ui/icons/24'
import { Plans } from 'blaze-ui/icons/20'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { Form, Formik } from 'formik'
import { useCallback, useEffect, useState, useRef } from 'react'
import { FormikHelpers } from 'formik/dist/types'
import Styles from './CampaignGeneration.module.scss'
import { CampaignGenerationStepProps, FormValues } from './Types'
import * as yup from 'yup'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { TextArea } from 'almanac-ui/TextInputField/Input'
import { Heading } from 'almanac-ui/Heading/Heading'
import { GrowthPlan } from 'blaze-ui/icons/20'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useRootStore } from 'stores/RootStore'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { VirtualMarketerClient } from 'api/clients/VirtualMarketerClient'
import { useBrandKitSources } from 'hooks/queries/useBrandKitSources'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { Text } from 'blaze-ui/Text/Text'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { collectResults } from 'utils/query'
import { useFileUpload } from 'components/FirstTimeExperienceModal/steps/BrandProfileDocuments/useFileUpload'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { useGenerateCampaignInputs } from 'hooks/queries/brandPlans/useGenerateCampaignInputs'
import { TextFieldLoadingState } from 'components/Wizards/TextFieldLoadingState/TextFieldLoadingState'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const MAX_FILES = 10

const validationSchema = yup.object().shape({
  growth_goals_input: yup.string(),
  key_dates_input: yup.string(),
})

const getFileIcon = (doc: any) => {
  const mimeType = doc.source?.mime_type

  if (mimeType === 'application/pdf') {
    return <Pdf size={20} color="var(--red-50)" />
  }
  if (mimeType === 'text/markdown') {
    return <Markdown size={20} color="var(--green-50)" />
  }
  if (mimeType === 'text/plain') {
    return <TextFile size={20} color="var(--teal-50)" />
  }
  if (mimeType?.includes('word') || mimeType?.includes('document')) {
    return <File size={20} color="var(--blue-50)" />
  }

  return <File size={20} color="var(--dark-80)" />
}

const getUploadButtonText = (isUploading: boolean, fileCount: number) => {
  if (isUploading) return 'Uploading...'
  if (fileCount >= MAX_FILES) return 'Max 10 files'
  if (fileCount > 0) return 'Upload more'
  return 'Upload your brand strategy docs'
}

const CampaignGenerationStep = (props: CampaignGenerationStepProps) => {
  const {
    onBack,
    onNext,
    initialValues,
    brandPlanId,
    brandKitId,
    skipSubscriptionUpdate = false,
    disablePlanChoice = false,
  } = props
  const workspace = useCurrentWorkspace()
  const { tierUsageStore } = useRootStore()
  const { autopilotPlans, currentSubscription, initialized, loading } = tierUsageStore
  const { isMobile } = useResponsiveDesign()
  const planChoiceFeatureFlag = useFlipperFlag('onboarding_plan_choice')
  const isPlanChoiceEnabled = planChoiceFeatureFlag && !disablePlanChoice
  const [selectedPlan, setSelectedPlan] = useState<'growth' | 'starter'>('growth')

  const { mutate: generateGoals, isLoading: isGeneratingGoals } = useGenerateCampaignInputs(brandPlanId || -1)
  const { mutate: generateDates, isLoading: isGeneratingDates } = useGenerateCampaignInputs(brandPlanId || -1)
  const [isGeneratingBoth, setIsGeneratingBoth] = useState(false)
  const hasInitialGeneratedRef = useRef(false)

  const { handleUpload, isLoading: isUploading } = useFileUpload(brandKitId || -1)

  const documentsQuery = useBrandKitSources(
    {
      brandKitId: brandKitId || -1,
      sourceType: ['SourceMaterialsMediaObject'],
    },
    {
      enabled: !!brandKitId,
      refetchOnWindowFocus: false,
    },
  )
  const documents = collectResults(documentsQuery, (data) => data.brand_kit_sources) || []

  const { mutate: deleteSource } = useDeleteBrandKitSource({
    onSuccess: () => {
      documentsQuery.refetch()
    },
  })

  useEffect(() => {
    if (!initialized && !loading && workspace) {
      tierUsageStore.fetch(workspace.id)
    }
  }, [initialized, loading, workspace, tierUsageStore, autopilotPlans.length, currentSubscription])

  const handleGenerateGoals = useCallback(
    (setFieldValue: (field: string, value: any) => void) => {
      generateGoals(undefined, {
        onSuccess: (data) => {
          setFieldValue('growth_goals_input', data.growth_goals_input)
        },
      })
    },
    [generateGoals],
  )

  const handleGenerateDates = useCallback(
    (setFieldValue: (field: string, value: any) => void) => {
      generateDates(undefined, {
        onSuccess: (data) => {
          setFieldValue('key_dates_input', data.key_dates_input)
        },
      })
    },
    [generateDates],
  )

  const handleGenerateBoth = useCallback(
    (setFieldValue: (field: string, value: any) => void) => {
      setIsGeneratingBoth(true)

      generateGoals(undefined, {
        onSuccess: (data) => {
          setFieldValue('growth_goals_input', data.growth_goals_input)
          setFieldValue('key_dates_input', data.key_dates_input)
          setIsGeneratingBoth(false)
        },
        onError: () => {
          setIsGeneratingBoth(false)
        },
      })
    },
    [generateGoals],
  )

  const handleFilesSelected = useCallback(
    async (fileList: FileList | null, setFieldValue?: (field: string, value: any) => void) => {
      if (!fileList || !brandKitId) return

      const currentCount = documents.length
      const availableSlots = Math.max(0, MAX_FILES - currentCount)

      if (availableSlots === 0) {
        showErrorMessageToast(`You can upload a maximum of ${MAX_FILES} files.`)
        return
      }

      const files = Array.from(fileList).slice(0, availableSlots)

      if (fileList.length > availableSlots) {
        showErrorMessageToast(
          `Only ${availableSlots} file${availableSlots > 1 ? 's' : ''} can be added. Maximum ${MAX_FILES} files total.`,
        )
      }

      await handleUpload(files)
      await documentsQuery.refetch()

      if (setFieldValue) {
        handleGenerateBoth(setFieldValue)
      }
    },
    [brandKitId, handleUpload, documentsQuery, documents.length, handleGenerateBoth],
  )

  const handleDeleteDocument = useCallback(
    (sourceId: number) => {
      if (!brandKitId) return
      deleteSource({ brandKitId, id: sourceId })
    },
    [brandKitId, deleteSource],
  )

  const handleSubmit = useCallback(
    async ({ growth_goals_input, key_dates_input }: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      try {
        if (skipSubscriptionUpdate) {
          onNext({ growth_goals_input, key_dates_input, selectedPlan })
          return
        }

        if (!workspace) throw new Error('No workspace')

        const targetPlanKey =
          selectedPlan === 'starter' ? PaymentPlanKey.BlazeAutopilotStarter : PaymentPlanKey.BlazeAutopilotGrowth

        if (currentSubscription?.plan === targetPlanKey) {
          onNext({ growth_goals_input, key_dates_input, selectedPlan })
          return
        }

        // ensure the plans are loaded
        if (autopilotPlans.length === 0) {
          await tierUsageStore.fetch(workspace.id)
        }
        const plans = autopilotPlans.length > 0 ? autopilotPlans : tierUsageStore.autopilotPlans
        const monthlyPrice = plans
          .find((p) => p.key === targetPlanKey)
          ?.prices?.find((p) => p.recurring.interval_count === 1)

        if (!monthlyPrice?.id) throw new Error('Plan not found')

        const res = await VirtualMarketerClient.updateSubscription(workspace.id, {
          price_id: monthlyPrice.id,
          should_end_trial: false,
        })

        if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Update failed')

        await tierUsageStore.fetch(workspace.id)
        onNext({ growth_goals_input, key_dates_input, selectedPlan })
      } catch (err) {
        showErrorMessageToast('Failed to proceed, please try again.')
        setSubmitting(false)
      }
    },
    [onNext, workspace, autopilotPlans, currentSubscription, tierUsageStore, selectedPlan, skipSubscriptionUpdate],
  )

  return (
    <MultiStepStepLayout title={'Choose your marketing strategy'}>
      <Formik
        initialValues={{
          growth_goals_input: initialValues?.growth_goals_input || '',
          key_dates_input: initialValues?.key_dates_input || '',
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ values, handleChange, handleBlur, errors, touched, submitForm, isSubmitting, setFieldValue }) => {
          // Trigger initial generation once when component mounts with empty fields
          if (
            !hasInitialGeneratedRef.current &&
            !values.growth_goals_input &&
            !values.key_dates_input &&
            !isGeneratingGoals &&
            !isGeneratingDates &&
            brandPlanId
          ) {
            hasInitialGeneratedRef.current = true
            handleGenerateBoth(setFieldValue)
          }

          return (
            <StickyFooterProvider
              footer={
                <StickyFooter>
                  <FooterButtons>
                    <Button
                      variant="tertiary"
                      size="xl"
                      onPress={onBack}
                      frontIcon={ArrowLeft}
                      aria-label="Back"
                      isDisabled={isSubmitting}
                    >
                      Back
                    </Button>
                    <Button
                      size="xl"
                      onPress={submitForm}
                      endIcon={ArrowRight}
                      aria-label="Continue"
                      isDisabled={isSubmitting}
                    >
                      Continue
                    </Button>
                  </FooterButtons>
                </StickyFooter>
              }
            >
              <Form className={isPlanChoiceEnabled ? Styles.planSelectionWrapper : Styles.form}>
                <div
                  className={(() => {
                    const baseClass = isPlanChoiceEnabled ? Styles.planContainer : Styles.inputWrapper
                    if (!isPlanChoiceEnabled) return baseClass
                    const selectionClass = selectedPlan === 'growth' ? Styles.selectedPlan : Styles.unselectedPlan
                    return `${baseClass} ${selectionClass}`
                  })()}
                  onClick={() => isPlanChoiceEnabled && setSelectedPlan('growth')}
                >
                  {isPlanChoiceEnabled && (
                    <div className={Styles.planRadio}>
                      <RadioBullet isSelected={selectedPlan === 'growth'} size={20} />
                    </div>
                  )}
                  <div className={Styles.growthPlanLabel}>
                    <GrowthPlan size={20} />
                    <span>Growth Plan</span>
                  </div>
                  {isPlanChoiceEnabled && (
                    <div className={Styles.growthContent}>
                      <div className={Styles.growthHeading}>
                        <Heading level={3}>I want targeted campaigns for the next year.</Heading>
                      </div>
                      <p className={Styles.growthDescription}>
                        Advanced marketing tools like parallel campaigns, content approvals, crossposting, and
                        AI-powered ad management for teams.
                      </p>
                    </div>
                  )}
                  {isPlanChoiceEnabled && <div className={Styles.dividerLine} />}
                  {isPlanChoiceEnabled && (
                    <div className={Styles.setupSection}>
                      <Heading level={2}>Setup your campaign strategy</Heading>
                    </div>
                  )}
                  <div className={Styles.inputContainer}>
                    {brandKitId && (
                      <div className={Styles.documentsSection}>
                        <div className={Styles.documentsHeading}>
                          <Heading level={4}>Upload any strategy docs you have</Heading>
                        </div>
                        <div className={Styles.documentsDescription}>
                          Ex: Documents and presentations that explain your marketing strategy
                        </div>

                        {documents.length > 0 && (
                          <div className={Styles.documentsList}>
                            {documents.map((doc) => (
                              <div key={doc.id} className={Styles.documentRow}>
                                <div className={Styles.documentInfo}>
                                  {getFileIcon(doc)}
                                  <div className={Styles.fileName}>
                                    <Text variant="smallList" lineClamp={1}>
                                      {doc.name || 'Document'}
                                    </Text>
                                  </div>
                                </div>
                                <IconButton
                                  icon={Trash2}
                                  size="sm"
                                  variant="ghost"
                                  onPress={() => handleDeleteDocument(doc.id)}
                                  aria-label="Delete file"
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        <div className={Styles.documentButtons}>
                          <FileTrigger
                            onSelect={(files) => handleFilesSelected(files, setFieldValue)}
                            accept=".pdf,.doc,.docx,.txt,.md"
                            allowsMultiple={true}
                            aria-label="Upload Files"
                          >
                            <Button
                              variant="ghost-tertiary"
                              size={isMobile ? 'lg' : 'md'}
                              frontIcon={Upload5}
                              fullWidth={isMobile}
                              isDisabled={isUploading || documentsQuery.isLoading || documents.length >= MAX_FILES}
                            >
                              {getUploadButtonText(isUploading, documents.length)}
                            </Button>
                          </FileTrigger>
                        </div>
                      </div>
                    )}

                    <div className={Styles.field}>
                      <div className={Styles.headingGroup}>
                        <Heading level={4}>What are your key growth goals for the next 12 months?</Heading>
                        <IconButton
                          icon={ArrowRefresh2}
                          size="md"
                          variant="ghost"
                          onPress={() => handleGenerateGoals(setFieldValue)}
                          isDisabled={isGeneratingGoals || isGeneratingBoth}
                          aria-label="Regenerate growth goals"
                        />
                      </div>
                      {isGeneratingGoals || isGeneratingBoth ? (
                        <TextFieldLoadingState lines={4} />
                      ) : (
                        <TextArea
                          id="growth_goals_input"
                          name="growth_goals_input"
                          value={values.growth_goals_input}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g. Double revenue; Launch a new product..."
                          className={Styles.textArea}
                          isInvalid={touched.growth_goals_input && !!errors.growth_goals_input}
                          errorMessage={touched.growth_goals_input ? errors.growth_goals_input : undefined}
                        />
                      )}
                    </div>
                    <div className={Styles.field}>
                      <div className={Styles.headingGroup}>
                        <Heading level={4}>
                          What are key dates, holidays, or milestones for your business or industry?
                        </Heading>
                        <IconButton
                          icon={ArrowRefresh2}
                          size="md"
                          variant="ghost"
                          onPress={() => handleGenerateDates(setFieldValue)}
                          isDisabled={isGeneratingDates || isGeneratingBoth}
                          aria-label="Regenerate key dates"
                        />
                      </div>
                      {isGeneratingDates || isGeneratingBoth ? (
                        <TextFieldLoadingState lines={4} />
                      ) : (
                        <TextArea
                          id="key_dates_input"
                          name="key_dates_input"
                          value={values.key_dates_input}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g. September 12th is our product launch, Black Friday is huge for us, tax filing day is April 15th..."
                          className={Styles.textArea}
                          isInvalid={touched.key_dates_input && !!errors.key_dates_input}
                          errorMessage={touched.key_dates_input ? errors.key_dates_input : undefined}
                        />
                      )}
                    </div>
                  </div>
                </div>
                {isPlanChoiceEnabled && (
                  <div
                    className={`${Styles.planContainer} ${Styles.starterPlanContainer} ${
                      selectedPlan === 'starter' ? Styles.selectedPlan : Styles.unselectedPlan
                    }`}
                    onClick={() => setSelectedPlan('starter')}
                  >
                    <div className={Styles.planRadio}>
                      <RadioBullet isSelected={selectedPlan === 'starter'} size={20} />
                    </div>
                    <div className={Styles.starterPlanLabel}>
                      <Plans size={20} />
                      <span>Starter Plan</span>
                    </div>
                    <div className={Styles.starterContent}>
                      <div className={Styles.starterHeading}>
                        <Heading level={3}>I need simple, weekly batches of content.</Heading>
                      </div>
                      <p className={Styles.starterDescription}>
                        Core tools for individuals creating and publishing content across up to 3 channels.
                      </p>
                    </div>
                  </div>
                )}
              </Form>
            </StickyFooterProvider>
          )
        }}
      </Formik>
    </MultiStepStepLayout>
  )
}

export { CampaignGenerationStep as CampaignGeneration }
